import { useMemo, useState } from 'react'
import { Download } from 'lucide-react'
import { useNavigate, useParams } from 'react-router'

import type { SubjectResponse } from '@/api/types/Subject'
import { AlertAssetTypes, AlertStatuses, type AlertResponse } from '@/api/types/Alert'
import { useGetAlertBySubjectId, useGetAlertCountsBySubjectId, useGetAlertSources } from '@/api/alert'
import { useGetPrivateSubject } from '@/api/profile'
import TalentCard from '@/components/TalentCard'
import Tabs from '@/components/Tabs'
import Button from '@/elements/Button'
import Text from '@/elements/Text'
import WatchdogAlertList from '@/components/WatchdogAlertList'
import ExportModal from '@/components/ExportModal'
import watchdogTabs, { type WatchdogTabsKeys } from '@/router/tabsSystems/watchdogTabs'
import BackLink from '@/elements/BackLink'
import Loading from '@/elements/Loading'
import Flex from '@/elements/Flex'
import useRoles from '@/hooks/useRoles'
import usePagination from '@/hooks/usePagination'
import FormSelect from '@/elements/FormSelect'
import { formatAsTitle } from '@/utils/format'
import useFiltering from '@/hooks/useFiltering'
import useBreakpoints from '@/hooks/useBreakpoints'

function buildExternalSourceFilters(externalSource: string | undefined) {
  if (!externalSource) return {}
  return Object.fromEntries(
    externalSource.split(',')
      .map(r => r.trim()).filter(Boolean)
      .map((source, idx) => [`filter[externalSource][${idx}]`, source])
  )
}

function Watchdog() {
  const navigate = useNavigate()
  const { isSubject } = useRoles()
  const { isTablet } = useBreakpoints()
  const [showExportModal, setShowExportModal] = useState(false)
  const { updateFilterBy, allFilters } = useFiltering<['externalSource', 'assetType']>()
  const { id: subjectId, status } = useParams<{ id: string, status: WatchdogTabsKeys }>()

  // Memoize filter params for API calls
  const alertFilters = useMemo(() => ({
    ...(status === 'all' ? undefined : status && { 'filter[status]': status.toUpperCase() }),
    ...buildExternalSourceFilters(allFilters.externalSource),
    ...(allFilters.assetType ? { 'filter[assetType]': allFilters.assetType } : {}),
  }), [status, allFilters.externalSource, allFilters.assetType])

  const alertCountsFilters = useMemo(() => ({
    ...buildExternalSourceFilters(allFilters.externalSource),
    ...(allFilters.assetType ? { 'filter[assetType]': allFilters.assetType } : {}),
  }), [allFilters.externalSource, allFilters.assetType])

  const { data: subjectProfileData, isLoading: subjectLoading } = useGetPrivateSubject(subjectId, true)
  const { data: alertCounts } = useGetAlertCountsBySubjectId(subjectId, alertCountsFilters)
  const { page, limit, setPage } = usePagination(10)
  const { data: alertsData, isLoading: alertsLoading } = useGetAlertBySubjectId({
    subjectId, page, limit, filters: alertFilters
  })
  const { data: alertSources } = useGetAlertSources(subjectId)

  // Memoize filter options for selects
  const alertFiltersBySource = useMemo(() =>
    alertSources?.data?.map(source => ({ label: formatAsTitle(source), value: source })) || [],
    [alertSources?.data]
  )
  const alertFiltersByAssetType = useMemo(() =>
    AlertAssetTypes.map(assetType => ({ label: formatAsTitle(assetType), value: assetType })),
    []
  )

  const subjectProfile: SubjectResponse | null | undefined = subjectProfileData?.data
  const alerts: AlertResponse[] = alertsData?.data?.items || []

  const tabsExtraDataFromAlerts = useMemo(() => {
    const alertCountsTotal = alertCounts?.data?.total || 0
    const alertCountsData = alertCounts?.data?.counts || {}
    return {
      all: `(${alertCountsTotal})`,
      ...Object.fromEntries(
        AlertStatuses.map(status => [status.toLowerCase(), `(${alertCountsData[status] || 0})`])
      )
    } as Record<WatchdogTabsKeys, string>
  }, [alertCounts])

  const handleClearFilters = () => {
    if (alerts.length === 0) {
      navigate(`/watchdog/${subjectId}/`)
    }
  }

  return (
    <Flex component='section' gap={4} grow>
      {!isSubject && (
        <BackLink onBackOverride={() => navigate('/dashboard')} text='Back to Dashboard' />
      )}
      <Flex inline={!isTablet} justify='space-between' wfull gap={3} wrap>
        <Text variant='h1'>Watchdog</Text>
        <Flex wfull inline gap={3} wrap justify='end' align='end' grow>
          <FormSelect
            name='source-filter'
            allowUnselect
            label='Filter by'
            placeholder='Source'
            options={alertFiltersBySource}
            values={alertFiltersBySource.filter(option => option.value === allFilters.externalSource)}
            onChange={([v]) => updateFilterBy('externalSource', v?.value)}
            optionLabel={item => item.label}
            optionValue={item => item.value}
            style={{ width: isTablet ? '100%' : 220 }}
          />
          <FormSelect
            name='asset-type-filter'
            allowUnselect
            placeholder='Asset Type'
            options={alertFiltersByAssetType}
            values={alertFiltersByAssetType.filter(option => option.value === allFilters.assetType)}
            onChange={([v]) => updateFilterBy('assetType', v?.value ?? '')}
            optionLabel={item => item.label}
            optionValue={item => item.value}
            style={{ width: isTablet ? '100%' : 220 }}
          />
          {subjectId && (
            <Button
              variant='iconBtn'
              onClick={() => setShowExportModal(true)}
              styles={{ width: 54, height: 54, minWidth: 54, minHeight: 54 }}
            >
              <Download size={20} />
            </Button>
          )}
        </Flex>
      </Flex>
      {subjectLoading ? (
        <Loading />
      ) : !subjectProfile?.user ? (
        <Flex wfull hfull align='center' justify='center' py={4} px={4}>
          <Text color='black-light-60'>No user information available.</Text>
        </Flex>
      ) : (
        <>
          <TalentCard
            subject={subjectProfile}
            fromWatchdog
            notificationCount={subjectProfileData?.data?.alertsCount}
          />
          <Tabs
            tabs={watchdogTabs}
            extraInfo={tabsExtraDataFromAlerts}
            keepQueryParamsButExclude={['page']}
            grow
          >
            {alertsLoading ? (
              <Flex wfull py={8} grow>
                <Loading />
              </Flex>
            ) : subjectId ? (
              <WatchdogAlertList
                alerts={alerts}
                pagination={alertsData?.data?.pagination}
                clearFilters={handleClearFilters}
                onPageChange={setPage}
              />
            ) : null}
          </Tabs>
        </>
      )}
      {subjectId && (
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          mode='individual'
          subjectId={subjectId}
        />
      )}
    </Flex>
  )
}

export default Watchdog
